import { produce, Draft } from 'immer'
import filter from 'lodash/filter'
import map from 'lodash/map'

import {
  IAction,
  TActionTypes
} from '@Root/types'

import {
  IElementState,
  TElementActionTypes,
  ElementActionTypes,
  IFetchElementsSuccessResponseBody,
  IDeleteElementSuccessPayload,
} from './types'

import {
  WorkspaceActionTypes
} from '@SiteModules/Workspace/types'

import {
  TProjectActionTypes,
  ProjectActionTypes,
  IDeleteProjectsSuccessPayload,
} from '@SiteModules/Project/types'

export const initialState: IElementState = {
  hasLoadedInitially: false,
  loading: false,
  deleting: false,
  elements: [],
  page: {
    totalCount: 0,
    totalPages: 1,
    currentPage: 0,
    shouldCheckAutoFill: false,
  }
}

type TAction = TActionTypes | TElementActionTypes | TProjectActionTypes

export const elementsReducer = (
  state: IElementState = initialState,
  action: TAction,
): IElementState => {
  return produce(state, draft => {
    switch (action.type) {
      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
      case WorkspaceActionTypes.RESET_CURRENT_WORKSPACE:
        resetElements(draft)
        break

      case ElementActionTypes.FETCH_ELEMENTS:
        fetchElements(draft)
        break

      case ElementActionTypes.FETCH_ELEMENTS_SUCCESS:
        fetchElementsSuccess(draft, action)
        break

      case ElementActionTypes.FETCH_ELEMENTS_FAILURE:
        fetchElementsFailure(draft)
        break

      case ElementActionTypes.DELETE_ELEMENT:
        deleteElement(draft)
        break

      case ElementActionTypes.DELETE_ELEMENT_SUCCESS:
        deleteElementSuccess(draft, action)
        break

      case ElementActionTypes.DELETE_ELEMENT_FAILURE:
        resetDeleteElement(draft)
        break

      case ElementActionTypes.RESET_SHOULD_CHECK_AUTO_FILL:
        resetShouldCheckAutoFill(draft)
        break

      case ElementActionTypes.SET_SHOULD_CHECK_AUTO_FILL:
        setShouldCheckAutoFill(draft)
        break

      case ProjectActionTypes.DELETE_PROJECTS_SUCCESS:
        updateElementsAfterDeletingProjects(draft, action)
        break

      default:
        break
    }
  })
}

const resetElements = (draft: Draft<IElementState>) => {
  draft.hasLoadedInitially = false
  draft.elements = []
}

const fetchElements = (draft: Draft<IElementState>) => {
  draft.loading = true
}

function isIFetchElementsSuccessAction(arg: any): arg is IAction<IFetchElementsSuccessResponseBody> {
  return arg.type === ElementActionTypes.FETCH_ELEMENTS_SUCCESS
}

const fetchElementsSuccess = (draft: Draft<IElementState>, action: TAction) => {
  if (!isIFetchElementsSuccessAction(action)) { return }

  if (action.payload.currentPage === 1 || action.payload.currentPage === draft.page.currentPage + 1) {
    if (action.payload.currentPage === 1) {
      draft.elements = action.payload.data
      draft.page.shouldCheckAutoFill = true
    } else {
      draft.elements.push(...action.payload.data)
    }

    draft.page.totalCount = action.payload.totalCount
    draft.page.totalPages = action.payload.totalPages
    draft.page.currentPage = action.payload.currentPage
  }

  draft.hasLoadedInitially = true
  draft.loading = false
}

const fetchElementsFailure = (draft: Draft<IElementState>) => {
  draft.loading = false
}

const deleteElement = (draft: Draft<IElementState>) => {
  draft.deleting = true
}

function isIDeleteElementSuccessAction(arg: any): arg is IAction<IDeleteElementSuccessPayload> {
  return arg.type === ElementActionTypes.DELETE_ELEMENT_SUCCESS
}

const deleteElementSuccess = (draft: Draft<IElementState>, action: TAction) => {
  if (!isIDeleteElementSuccessAction(action)) { return }

  draft.elements = filter(draft.elements, element => element.id !== action.payload.elementId)
  draft.deleting = false
}

const resetDeleteElement = (draft: Draft<IElementState>) => {
  draft.deleting = false
}

const resetShouldCheckAutoFill = (draft: Draft<IElementState>) => {
  draft.page.shouldCheckAutoFill = false
}

const setShouldCheckAutoFill = (draft: Draft<IElementState>) => {
  draft.page.shouldCheckAutoFill = true
}

function isIDeleteProjectsSuccessAction(arg: any): arg is IAction<IDeleteProjectsSuccessPayload> {
  return arg.type === ProjectActionTypes.DELETE_PROJECTS_SUCCESS
}

const updateElementsAfterDeletingProjects = (draft: Draft<IElementState>, action: TAction) => {
  if (!isIDeleteProjectsSuccessAction(action)) { return }

  draft.elements = map(draft.elements, element => {
    const updatedProjectIds = filter(element.attributes.projectIds, projectId => !action.payload.projectIds.includes(projectId))

    return ({ ...element, attributes: { ...element.attributes, projectVersionIds: updatedProjectIds} })
  })
}
